import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  ShoppingCartIcon, 
  HeartIcon, 
  ShareIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const { formatPrice } = useCurrency();
  const { fetchCartItems } = useCart();
  const { isAuthenticated, getToken } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [addingToCart, setAddingToCart] = useState(false);
  const [zoomState, setZoomState] = useState({ active: false, x: 50, y: 50 });
  const [touchStartX, setTouchStartX] = useState(null);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const queuePendingCartAction = useCallback((redirectTo) => {
    if (!product?.id) return;

    try {
      window.sessionStorage.setItem('pending_cart_action', JSON.stringify({
        productId: product.id,
        quantity,
        redirectTo,
        from: `${location.pathname}${location.search}${location.hash}`
      }));
    } catch (error) {
      console.error('Failed to store pending cart action:', error);
    }
  }, [location.hash, location.pathname, location.search, product?.id, quantity]);

  // Scroll to top when product changes (immediate, then smooth on load)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' }); // Immediate scroll
  }, [id]);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const url = `${API_BASE}/api/products/${id}`;
      
      console.log('🔍 [ProductDetail] Fetching product:', { id, url, API_BASE });
      
      const response = await fetch(url);
      
      console.log('📥 [ProductDetail] Response status:', response.status, response.statusText);
      console.log('📥 [ProductDetail] Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Check if response is ok
      if (!response.ok) {
        console.error('❌ [ProductDetail] Response not OK:', response.status);
        if (response.status === 404) {
          setError('Product not found');
        } else if (response.status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError('Failed to load product. Please try again.');
        }
        return;
      }
      
      const data = await response.json();
      console.log('📦 [ProductDetail] Response data:', data);
      
      if (data.success) {
        // Handle both response formats: data.data or data.product
        const productData = data.data || data.product;
        console.log('✅ [ProductDetail] Product data:', productData);
        if (productData) {
          setProduct(productData);
          console.log('✅ [ProductDetail] Product set successfully');
        } else {
          console.error('❌ [ProductDetail] Product data not found in response:', data);
          setError('Product not found');
        }
      } else {
        console.error('❌ [ProductDetail] API returned error:', data.message);
        setError(data.message || 'Product not found');
      }
    } catch (error) {
      console.error('❌ [ProductDetail] Error fetching product:', error);
      console.error('❌ [ProductDetail] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setError('Cannot connect to server. Please make sure the backend server is running.');
      } else {
        setError('Failed to load product. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchReviews = useCallback(async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE}/api/products/${id}/reviews`);
      const data = await response.json();
      if (data.success) {
        setReviews(data.data);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  }, [id]);

  const fetchRelatedProducts = useCallback(async () => {
    if (!product) return;
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE}/api/products?category=${product.category_id}&limit=5`);
      const data = await response.json();
      if (data.success && data.products) {
        // Filter out current product
        const related = data.products.filter(p => p.id !== parseInt(id));
        setRelatedProducts(related.slice(0, 4));
      }
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  }, [id, product]);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
    // Smooth scroll after a brief delay to ensure page is ready
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [fetchProduct, fetchReviews, id]);

  useEffect(() => {
    if (product) {
      fetchRelatedProducts();
    }
  }, [fetchRelatedProducts, product]);

  const handleAddToCart = async () => {
    console.log('🛒 [ProductDetail] handleAddToCart called:', { product: product?.id, quantity });
    
    if (!product) {
      toast.error('Product information not available');
      return { success: false, message: 'Product not available' };
    }
    
    if (!product.stock_quantity || product.stock_quantity <= 0) {
      console.warn('⚠️ [ProductDetail] Product out of stock');
      toast.error('This product is out of stock');
      return { success: false, message: 'Out of stock' };
    }
    
    if (product.stock_quantity < quantity) {
      console.warn('⚠️ [ProductDetail] Insufficient stock:', { stock: product.stock_quantity, quantity });
      toast.error(`Only ${product.stock_quantity} item(s) available`);
      return { success: false, message: 'Insufficient stock' };
    }

    try {
      setAddingToCart(true);
      const token = getToken(false); // E-commerce token
      console.log('🔑 [ProductDetail] Token check:', token ? 'Token exists' : 'No token');
      
      if (!token) {
        console.warn('⚠️ [ProductDetail] User not authenticated');
        queuePendingCartAction(`${location.pathname}${location.search}${location.hash}`);
        toast.error('Please login to add items to cart');
        navigate('/login', { state: { from: location } });
        return { success: false, message: 'Not authenticated' };
      }

      const API_BASE = process.env.REACT_APP_API_URL || '';
      const url = `${API_BASE}/api/cart`;
      const payload = {
        product_id: product.id,
        quantity: quantity
      };
      
      console.log('📤 [ProductDetail] Adding to cart:', { url, payload, API_BASE });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      console.log('📥 [ProductDetail] Cart response status:', response.status);
      
      // Check if response is OK before parsing
      if (!response.ok) {
        let errorMessage = 'Failed to add to cart';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
          console.error('❌ [ProductDetail] Server error response:', errorData);
        } catch (parseError) {
          console.error('❌ [ProductDetail] Failed to parse error response:', parseError);
          errorMessage = `Server error (${response.status})`;
        }
        toast.error(errorMessage);
        return { success: false, message: errorMessage };
      }
      
      const data = await response.json();
      console.log('📦 [ProductDetail] Cart response data:', data);
      
      if (data.success) {
        console.log('✅ [ProductDetail] Item added to cart successfully');
        toast.success(`${product.name} added to cart!`, {
          icon: '🛒',
          duration: 3000,
        });
        fetchCartItems();
        return { success: true };
      } else {
        console.error('❌ [ProductDetail] Failed to add to cart:', data.message);
        toast.error(data.message || 'Failed to add to cart');
        return { success: false, message: data.message || 'Failed to add to cart' };
      }
    } catch (error) {
      console.error('❌ [ProductDetail] Error adding to cart:', error);
      console.error('❌ [ProductDetail] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toast.error('Failed to add to cart. Please try again.');
      return { success: false, message: 'Network error' };
    } finally {
      setAddingToCart(false);
    }
  };

  const handleBuyNow = async () => {
    console.log('🛍️ [ProductDetail] handleBuyNow called');
    console.log('🛍️ [ProductDetail] Current state:', { 
      product: product?.id, 
      isAuthenticated, 
      quantity,
      stock: product?.stock_quantity 
    });
    
    // Check if product is loaded
    if (!product) {
      console.warn('⚠️ [ProductDetail] Product not loaded yet');
      toast.error('Product information is still loading. Please wait.');
      return;
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
      console.warn('⚠️ [ProductDetail] User not authenticated');
      queuePendingCartAction('/checkout');
      toast.error('Please login to continue');
      navigate('/login', { state: { from: location } });
      return;
    }

    // Check stock availability
    if (product.stock_quantity < quantity) {
      console.warn('⚠️ [ProductDetail] Insufficient stock:', { 
        stock: product.stock_quantity, 
        quantity 
      });
      toast.error('Insufficient stock available');
      return;
    }

    if (product.stock_quantity === 0) {
      console.warn('⚠️ [ProductDetail] Product out of stock');
      toast.error('This product is out of stock');
      return;
    }

    try {
      console.log('🛍️ [ProductDetail] Starting add to cart process...');
      // Add to cart first (handleAddToCart manages its own loading state)
      const result = await handleAddToCart();
      console.log('🛍️ [ProductDetail] Add to cart result:', result);
      
      if (result && result.success === true) {
        console.log('✅ [ProductDetail] Successfully added to cart, navigating to checkout...');
        // Navigate to checkout after a short delay to ensure cart is updated
        setTimeout(() => {
          console.log('🛍️ [ProductDetail] Navigating to /checkout');
          navigate('/checkout');
        }, 500);
      } else {
        // Error message already shown by handleAddToCart, just log it
        console.error('❌ [ProductDetail] Failed to add to cart:', result?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('❌ [ProductDetail] Error in buy now:', error);
      console.error('❌ [ProductDetail] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toast.error('Failed to proceed to checkout. Please try again.');
    }
  };

  const handleWishlist = () => {
    setIsWishlisted(!isWishlisted);
    toast.success(isWishlisted ? 'Removed from wishlist' : 'Added to wishlist', {
      icon: isWishlisted ? '💔' : '❤️',
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: product.description,
          url: window.location.href,
        });
        toast.success('Shared successfully!');
      } catch (error) {
        // User cancelled or error
      }
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  // Get product images - prioritize images array from API, fallback to image_url, then placeholder
  // Must be called before any early returns (React Hook rules)
  const productImages = React.useMemo(() => {
    if (!product) return ['/api/placeholder/500/400'];
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images.map(img => img.image_url || img.url || img);
    }
    if (product.image_url) {
      return [product.image_url];
    }
    return ['/api/placeholder/500/400'];
  }, [product]);

  const goToImage = useCallback((index) => {
    setSelectedImage(index);
    setZoomState({ active: false, x: 50, y: 50 });
  }, []);

  const goToPrevImage = useCallback(() => {
    setSelectedImage((current) => {
      if (productImages.length <= 1) return current;
      return current === 0 ? productImages.length - 1 : current - 1;
    });
    setZoomState({ active: false, x: 50, y: 50 });
  }, [productImages.length]);

  const goToNextImage = useCallback(() => {
    setSelectedImage((current) => {
      if (productImages.length <= 1) return current;
      return current === productImages.length - 1 ? 0 : current + 1;
    });
    setZoomState({ active: false, x: 50, y: 50 });
  }, [productImages.length]);

  const handleImageMouseMove = useCallback((event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    setZoomState({ active: true, x, y });
  }, []);

  const handleTouchStart = useCallback((event) => {
    setTouchStartX(event.changedTouches[0]?.clientX ?? null);
  }, []);

  const handleTouchEnd = useCallback((event) => {
    if (touchStartX === null || productImages.length <= 1) return;

    const endX = event.changedTouches[0]?.clientX ?? touchStartX;
    const deltaX = touchStartX - endX;

    if (Math.abs(deltaX) < 40) {
      setTouchStartX(null);
      return;
    }

    if (deltaX > 0) {
      goToNextImage();
    } else {
      goToPrevImage();
    }

    setTouchStartX(null);
  }, [goToNextImage, goToPrevImage, productImages.length, touchStartX]);

  useEffect(() => {
    setSelectedImage(0);
    setZoomState({ active: false, x: 50, y: 50 });
    setIsTitleExpanded(false);
    setIsDescriptionExpanded(false);
  }, [id, productImages.length]);

  const twoLineClampStyle = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden'
  };

  const showTitleToggle = (product?.name || '').length > 90;
  const showDescriptionToggle = (product?.description || '').length > 180;
  
  const isOutOfStock = product ? (!product.stock_quantity || product.stock_quantity <= 0) : false;
  const avgRating = calculateAverageRating();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin absolute top-0 left-0 animate-pulse"></div>
          </div>
          <p className="mt-4 text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-xl">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{error || 'Product not found'}</h1>
          <button
            onClick={() => navigate('/products')}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 max-w-7xl">
        {/* Breadcrumb */}
        <nav className="mb-2 sm:mb-3 md:mb-4 text-xs sm:text-sm">
          <ol className="flex items-center space-x-1 sm:space-x-2 text-gray-600 flex-wrap">
            <li><Link to="/" className="hover:text-indigo-600 transition-colors">Home</Link></li>
            <li>/</li>
            <li><Link to="/products" className="hover:text-indigo-600 transition-colors">Products</Link></li>
            <li>/</li>
            <li className="text-gray-900 font-medium truncate max-w-[150px] sm:max-w-none">{product.name}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-12 mb-4 sm:mb-6 md:mb-8">
          {/* Product Image Gallery */}
          <div className="space-y-2 sm:space-y-3">
            <div
              className="relative bg-white rounded-lg shadow-md overflow-hidden group border border-gray-200"
              onMouseMove={handleImageMouseMove}
              onMouseEnter={() => setZoomState((prev) => ({ ...prev, active: true }))}
              onMouseLeave={() => setZoomState({ active: false, x: 50, y: 50 })}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <img
                src={productImages[selectedImage]}
                alt={product.name}
                className="w-full h-[250px] sm:h-[300px] md:h-[350px] lg:h-[400px] xl:h-[450px] object-contain transition-transform duration-200 ease-out cursor-zoom-in"
                style={{
                  width: '100%',
                  objectFit: 'contain',
                  padding: '10px',
                  transform: zoomState.active ? 'scale(1.85)' : 'scale(1)',
                  transformOrigin: `${zoomState.x}% ${zoomState.y}%`
                }}
                loading="lazy"
                onError={(e) => {
                  e.target.src = '/api/placeholder/500/400';
                }}
              />
              {productImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={goToPrevImage}
                    className="absolute left-3 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-700 shadow-lg transition hover:bg-white lg:flex"
                    aria-label="Previous image"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={goToNextImage}
                    className="absolute right-3 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-700 shadow-lg transition hover:bg-white lg:flex"
                    aria-label="Next image"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
              {/* Out of Stock Badge */}
              {isOutOfStock && (
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 md:top-4 md:left-4 bg-red-600 text-white px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 rounded-md sm:rounded-lg font-bold text-xs sm:text-sm shadow-lg z-10">
                  Out of Stock
                </div>
              )}
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 flex gap-1.5 sm:gap-2">
                <button
                  onClick={handleWishlist}
                  className="bg-white/90 backdrop-blur-sm p-2 sm:p-2.5 md:p-3 rounded-full hover:bg-white transition-all shadow-lg"
                >
                  {isWishlisted ? (
                    <HeartIconSolid className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-red-500" />
                  ) : (
                    <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-700" />
                  )}
                </button>
                <button
                  onClick={handleShare}
                  className="bg-white/90 backdrop-blur-sm p-2 sm:p-2.5 md:p-3 rounded-full hover:bg-white transition-all shadow-lg"
                >
                  <ShareIcon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-700" />
                </button>
              </div>
              {productImages.length > 1 && (
                <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5 rounded-full bg-black/25 px-3 py-2 backdrop-blur-sm lg:hidden">
                  {productImages.map((_, idx) => (
                    <button
                      key={`dot-${idx}`}
                      type="button"
                      onClick={() => goToImage(idx)}
                      className={`h-2.5 rounded-full transition-all ${selectedImage === idx ? 'w-5 bg-white' : 'w-2.5 bg-white/50'}`}
                      aria-label={`Go to image ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
            {productImages.length > 1 && (
              <div className="px-1 text-center text-xs text-gray-500 lg:hidden">
                Swipe left or right to view more images
              </div>
            )}
            {productImages.length > 1 && (
              <div className="hidden gap-2 sm:gap-3 lg:grid lg:grid-cols-5 xl:grid-cols-6">
                {productImages.map((img, idx) => (
                  <button
                    key={`img-${idx}-${img}`}
                    onClick={() => goToImage(idx)}
                    className={`rounded-md overflow-hidden border-2 transition-all hover:border-indigo-400 ${
                      selectedImage === idx ? 'border-indigo-500 ring-2 ring-indigo-200 shadow-md' : 'border-gray-200'
                    }`}
                  >
                    <img 
                      src={img} 
                      alt={`${product.name} ${idx + 1}`} 
                      className="w-full h-12 sm:h-16 md:h-20 object-contain"
                      style={{ width: '100%', objectFit: 'contain', padding: '2px' }}
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = '/api/placeholder/100/100';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-3 sm:space-y-4 md:space-y-5">
            <div>
              <h1
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2 leading-tight"
                style={isTitleExpanded ? undefined : twoLineClampStyle}
              >
                {product.name}
              </h1>
              {showTitleToggle && (
                <button
                  type="button"
                  onClick={() => setIsTitleExpanded((current) => !current)}
                  className="mb-2 text-sm font-semibold text-indigo-600 transition hover:text-indigo-700"
                >
                  {isTitleExpanded ? 'Read less' : 'Read more'}
                </button>
              )}
              {reviews.length > 0 && (
                <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon
                        key={i}
                        className={`w-3 h-3 sm:w-4 sm:h-4 ${
                          i < Math.round(avgRating) ? 'text-yellow-400 fill-current' : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs sm:text-sm text-gray-600">({avgRating}) • {reviews.length} reviews</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-baseline gap-2 sm:gap-3 pb-2 sm:pb-3 border-b border-gray-200">
              <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-indigo-600">{formatPrice(product.price)}</span>
              {product.original_price && product.original_price > product.price && (
                <>
                  <span className="text-lg sm:text-xl text-gray-400 line-through">{formatPrice(product.original_price)}</span>
                  <span className="text-xs sm:text-sm font-semibold text-green-600 bg-green-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                    {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
                  </span>
                </>
              )}
            </div>

            {product.description && (
              <div className="space-y-2">
                <h2 className="text-base font-semibold text-gray-900 sm:text-lg">About this item</h2>
                <p
                  className="text-sm sm:text-base text-gray-700 leading-relaxed"
                  style={isDescriptionExpanded ? undefined : twoLineClampStyle}
                >
                  {product.description}
                </p>
                {showDescriptionToggle && (
                  <button
                    type="button"
                    onClick={() => setIsDescriptionExpanded((current) => !current)}
                    className="text-sm font-semibold text-indigo-600 transition hover:text-indigo-700"
                  >
                    {isDescriptionExpanded ? 'Read less' : 'Read more'}
                  </button>
                )}
              </div>
            )}

            {/* Stock Status */}
            <div className="flex items-center gap-2 sm:gap-3">
              {!isOutOfStock ? (
                <>
                  <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
                  <span className="text-sm sm:text-base text-green-700 font-semibold">
                    {product.stock_quantity} in stock
                  </span>
                </>
              ) : (
                <>
                  <XCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                  <span className="text-sm sm:text-base text-red-700 font-semibold">Out of Stock</span>
                </>
              )}
            </div>

            {/* Quantity and Actions */}
            {!isOutOfStock ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Quantity</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="w-12 h-12 border-2 border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-bold text-lg"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 1;
                        setQuantity(Math.min(Math.max(1, val), Math.min(product.stock_quantity, 10)));
                      }}
                      min="1"
                      max={Math.min(product.stock_quantity, 10)}
                      className="w-20 h-12 text-center border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-semibold text-lg"
                    />
                    <button
                      onClick={() => setQuantity(Math.min(product.stock_quantity, 10, quantity + 1))}
                      disabled={quantity >= Math.min(product.stock_quantity, 10)}
                      className="w-12 h-12 border-2 border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-bold text-lg"
                    >
                      +
                    </button>
                    <span className="text-gray-500 text-sm ml-2">Max: {Math.min(product.stock_quantity, 10)}</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleAddToCart}
                    disabled={addingToCart || isOutOfStock}
                    className="flex-1 bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isOutOfStock ? 'Out of Stock' : ''}
                  >
                    {addingToCart ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <ShoppingCartIcon className="w-5 h-5" />
                        Add to Cart
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (isOutOfStock) {
                        toast.error('This product is out of stock');
                        return;
                      }
                      handleBuyNow();
                    }}
                    disabled={addingToCart || isOutOfStock}
                    className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all duration-200 shadow-md hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isOutOfStock ? 'Out of Stock' : ''}
                  >
                    Buy Now
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
                <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <p className="text-red-700 font-semibold text-lg mb-2">This product is currently out of stock</p>
                <p className="text-red-600 text-sm">Please check back later or contact us for availability</p>
              </div>
            )}

            {/* Product Details */}
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 md:p-5 border border-gray-200">
              <h3 className="text-sm sm:text-base font-bold mb-2 sm:mb-3 text-gray-900">Product Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Category:</span>
                  <span className="text-gray-900 font-semibold">{product.category_name || 'N/A'}</span>
                </div>
                {product.sku && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600 font-medium">SKU:</span>
                    <span className="text-gray-900 font-semibold">{product.sku}</span>
                  </div>
                )}
                {product.weight && (
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600 font-medium">Weight:</span>
                    <span className="text-gray-900 font-semibold">{product.weight}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-4 sm:mt-6 md:mt-8 bg-white rounded-lg shadow-md p-4 sm:p-5 md:p-6 border border-gray-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-5 md:mb-6 gap-2">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Customer Reviews</h2>
            {reviews.length > 0 && (
              <div className="text-left sm:text-right">
                <div className="text-xl sm:text-2xl font-bold text-indigo-600">{avgRating}</div>
                <div className="text-xs text-gray-600">Based on {reviews.length} reviews</div>
              </div>
            )}
          </div>
          
          {reviews.length === 0 ? (
            <div className="text-center py-8">
              <StarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-1">No reviews yet</p>
              <p className="text-gray-400 text-sm">Be the first to review this product!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:shadow-sm transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                          {review.user_name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{review.user_name || 'Anonymous'}</div>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <StarIcon
                                key={i}
                                className={`w-4 h-4 ${
                                  i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <span className="text-gray-500 text-sm">
                      {new Date(review.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-4 sm:mt-6 md:mt-8">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">You May Also Like</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/products/${relatedProduct.id}`}
                  className="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-200"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={relatedProduct.image || relatedProduct.image_url || '/api/placeholder/400/300'}
                      alt={relatedProduct.name}
                      className="w-full h-40 object-contain group-hover:scale-105 transition-transform duration-300"
                      style={{ padding: '10px' }}
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold mb-1 text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
                      {relatedProduct.name}
                    </h3>
                    <div className="text-indigo-600 font-bold text-base">
                      {formatPrice(relatedProduct.price)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
